import { LDFlagSet } from 'launchdarkly-react-client-sdk';
import { memoize } from 'micro-memoize';

import { ApiView, ApiViewState, getDefaultApiViewState } from '../../../core/ApiView';
import { currentUserQuery } from '../../../queries/currentUser';
import { ExtendedReportTemplateList, processReportTemplates } from '../../../processors/reports/reportTemplatesList';
import { membersQuery } from '../../../queries/members';
import { reportTemplatesQuery } from '../../../queries/reportTemplates';

export interface ReportTemplatesListViewState extends ApiViewState {
  futrliTemplates: ExtendedReportTemplateList[];
  futrliReportTemplates: ExtendedReportTemplateList[];
  futrliDashboardTemplates: ExtendedReportTemplateList[];
  workspaceTemplates: ExtendedReportTemplateList[];
  workspaceReportTemplates: ExtendedReportTemplateList[];
  workspaceDashboardTemplates: ExtendedReportTemplateList[];
  publishedWorkspaceReportTemplates: ExtendedReportTemplateList[];
  publishedWorkspaceDashboardTemplates: ExtendedReportTemplateList[];
}

export class ReportTemplatesListView extends ApiView {
  private currentUserObs?: ReturnType<typeof currentUserQuery>;
  private membersObs?: ReturnType<typeof membersQuery>;
  private reportTemplatesObs: ReturnType<typeof reportTemplatesQuery>;

  private memoizedProcessReportTemplates: typeof processReportTemplates;

  public state: ReportTemplatesListViewState = {
    ...getDefaultApiViewState(),
    futrliTemplates: [],
    futrliReportTemplates: [],
    futrliDashboardTemplates: [],
    workspaceTemplates: [],
    workspaceReportTemplates: [],
    workspaceDashboardTemplates: [],
    publishedWorkspaceReportTemplates: [],
    publishedWorkspaceDashboardTemplates: []
  };

  constructor(workspaceId: string, organisationId: string, isConsolidation = false, private flags: LDFlagSet = {}) {
    super();

    if (flags.resourceSharing) {
      this.currentUserObs = this.addQuery(currentUserQuery());
      this.membersObs = this.addQuery(membersQuery(workspaceId, organisationId, isConsolidation));
    }
    this.reportTemplatesObs = this.addQuery(reportTemplatesQuery(workspaceId, organisationId, isConsolidation));

    this.memoizedProcessReportTemplates = memoize(processReportTemplates);

    this.updateState();
  }

  // ****************************
  // * Private / Protected API *
  // ****************************
  private get allTemplates(): ExtendedReportTemplateList[] {
    const currentUser = this.currentUserObs ? this.getResult(this.currentUserObs).data : undefined;
    const members = this.membersObs ? this.getResult(this.membersObs).data ?? [] : [];

    return this.memoizedProcessReportTemplates(
      this.getResult(this.reportTemplatesObs).data ?? [],
      this.flags,
      currentUser,
      members
    );
  }

  private get futrliTemplates(): ExtendedReportTemplateList[] {
    return this.allTemplates.filter(template => template.kind === 'futrli' && template.available_area !== 'onboarding');
  }

  private get futrliReportTemplates(): ExtendedReportTemplateList[] {
    return this.futrliTemplates.filter(template => template.orientation !== 'dashboard');
  }

  private get futrliDashboardTemplates(): ExtendedReportTemplateList[] {
    return this.futrliTemplates.filter(template => template.orientation === 'dashboard');
  }

  private get workspaceTemplates(): ExtendedReportTemplateList[] {
    return this.allTemplates.filter(
      template => template.kind === 'workspace' && template.available_area !== 'onboarding'
    );
  }

  private get workspaceReportTemplates(): ExtendedReportTemplateList[] {
    return this.workspaceTemplates.filter(template => template.orientation !== 'dashboard');
  }

  private get workspaceDashboardTemplates(): ExtendedReportTemplateList[] {
    return this.workspaceTemplates.filter(template => template.orientation === 'dashboard');
  }

  private get publishedWorkspaceReportTemplates(): ExtendedReportTemplateList[] {
    return this.flags.resourceSharing
      ? this.workspaceReportTemplates
      : this.workspaceReportTemplates.filter(template => template.status === 'published');
  }

  private get publishedWorkspaceDashboardTemplates(): ExtendedReportTemplateList[] {
    return this.flags.resourceSharing
      ? this.workspaceDashboardTemplates
      : this.workspaceDashboardTemplates.filter(template => template.status === 'published');
  }

  protected getNewState(): ReportTemplatesListViewState {
    return {
      ...super.getNewState(),
      futrliTemplates: this.futrliTemplates,
      futrliReportTemplates: this.futrliReportTemplates,
      futrliDashboardTemplates: this.futrliDashboardTemplates,
      workspaceTemplates: this.workspaceTemplates,
      workspaceReportTemplates: this.workspaceReportTemplates,
      workspaceDashboardTemplates: this.workspaceDashboardTemplates,
      publishedWorkspaceReportTemplates: this.publishedWorkspaceReportTemplates,
      publishedWorkspaceDashboardTemplates: this.publishedWorkspaceDashboardTemplates
    };
  }
}
